// "Résumé" tab — KPI grid + EUDR breakdown for the OT. The legacy
// delivery-detail summary is per-delivery; here we aggregate across
// every delivery feeding the OT.

import { useTranslation } from "react-i18next"
import {
  AlertTriangle,
  CheckCircle2,
  MapPin,
  Receipt,
  Ship,
  Users,
} from "lucide-react"

import { Card } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import type { OT, OTTraceability } from "@/features/transit/api/types"

interface Props {
  ot: OT
  traceability: OTTraceability
}

export function TraceabilitySummaryTab({ ot, traceability }: Props) {
  const { t } = useTranslation("transit")
  const { summary, deliveries } = traceability

  const traceablePct =
    summary.producers.total === 0
      ? 0
      : Math.round(
          (summary.producers.traceable / summary.producers.total) * 100,
        )

  return (
    <div className="flex flex-col gap-5">
      <Card className="flex flex-col gap-4 p-5">
        <SectionHeader
          title={t("trace.summary.shipmentTitle")}
          subtitle={ot.label}
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Fact label={t("trace.summary.client")} value={ot.clientName} />
          <Fact
            label={t("trace.summary.contract")}
            value={ot.contractNumber ?? `#${ot.contractId}`}
          />
          <Fact
            label={t("trace.summary.vessel")}
            value={[ot.vessel, ot.voyage].filter(Boolean).join(" · ") || "—"}
          />
          <Fact
            label={t("trace.summary.route")}
            value={joinRoute(ot.pol, ot.pod)}
          />
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <SectionHeader
          title={t("trace.summary.eudrTitle")}
          subtitle={t("trace.summary.eudrSubtitle")}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <KpiTile
            icon={<Users className="size-5" />}
            label={t("trace.summary.kpi.producers")}
            primary={`${summary.producers.compliant}/${summary.producers.total}`}
            secondary={t("trace.summary.kpi.producersSub", {
              traceable: summary.producers.traceable,
              total: summary.producers.total,
              percent: traceablePct,
            })}
            tone={
              summary.producers.compliant === summary.producers.total
                ? "good"
                : "warn"
            }
          />
          <KpiTile
            icon={<MapPin className="size-5" />}
            label={t("trace.summary.kpi.parcels")}
            primary={`${summary.parcels.compliant}/${summary.parcels.total}`}
            secondary={t("trace.summary.kpi.parcelsSub")}
            tone={
              summary.parcels.compliant === summary.parcels.total
                ? "good"
                : "bad"
            }
          />
          <KpiTile
            icon={<Receipt className="size-5" />}
            label={t("trace.summary.kpi.purchases")}
            primary={summary.purchases.total.toLocaleString()}
            secondary={t("trace.summary.kpi.purchasesSub", {
              kg: summary.purchases.totalKg.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              }),
              bags: summary.purchases.totalBags.toLocaleString(),
            })}
            tone="neutral"
          />
        </div>

        <Separator className="my-1" />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Verdict
            label={t("trace.summary.verdict.eudr")}
            ok={summary.deliveries.status === "PASS"}
            okText={t("terminer.eudrStatus.PASS")}
            koText={t(`terminer.eudrStatus.${summary.deliveries.status}`)}
          />
          <Verdict
            label={t("trace.summary.verdict.traceability")}
            ok={traceablePct === 100}
            okText={t("trace.summary.verdict.traceComplete")}
            koText={t("trace.summary.verdict.traceIncomplete", {
              percent: traceablePct,
            })}
          />
          <Verdict
            label={t("trace.summary.verdict.deliveries")}
            ok={summary.deliveries.failed === 0 && summary.deliveries.total > 0}
            okText={t("trace.summary.verdict.deliveriesOk", {
              compliant: summary.deliveries.compliant,
              total: summary.deliveries.total,
            })}
            koText={t("trace.summary.verdict.deliveriesKo", {
              failed: summary.deliveries.failed,
              total: summary.deliveries.total,
            })}
          />
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <SectionHeader
          title={t("trace.summary.deliveriesTitle")}
          subtitle={t("trace.summary.deliveriesSubtitle", {
            count: deliveries.length,
          })}
        />
        {deliveries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("trace.summary.deliveriesEmpty")}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {deliveries.map((d) => (
              <li
                key={d.id}
                className="flex items-start gap-2 rounded-md border bg-card px-3 py-2"
              >
                <Ship className="mt-0.5 size-4 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {d.billOfLadingNumber ??
                      d.shippingFormNumber ??
                      `#${d.id}`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {d.cooperativeName ?? "—"} · {d.producerCount}{" "}
                    {t("trace.summary.producersWord")} ·{" "}
                    {d.traceabilityStatus}%{" "}
                    {t("trace.summary.traceWord")}
                  </div>
                </div>
                <DeliveryStateChip
                  isCompliant={d.isCompliant}
                  isLocked={d.isLocked}
                  hasRisks={d.hasRisks}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <h2 className="text-sm font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      ) : null}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-medium text-foreground">
        {value || "—"}
      </div>
    </div>
  )
}

type Tone = "good" | "warn" | "bad" | "neutral"

function KpiTile({
  icon,
  label,
  primary,
  secondary,
  tone,
}: {
  icon: React.ReactNode
  label: string
  primary: string
  secondary: string
  tone: Tone
}) {
  const palette: Record<Tone, string> = {
    good: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    warn: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    bad: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    neutral: "bg-muted text-foreground",
  }
  return (
    <div className="flex items-center gap-3 rounded-md border bg-card p-3">
      <div
        className={`flex size-10 flex-shrink-0 items-center justify-center rounded-md ${palette[tone]}`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
          {primary}
        </div>
        <div className="truncate text-[11px] text-muted-foreground">
          {secondary}
        </div>
      </div>
    </div>
  )
}

function Verdict({
  label,
  ok,
  okText,
  koText,
}: {
  label: string
  ok: boolean
  okText: string
  koText: string
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border bg-card px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        {ok ? (
          <CheckCircle2 className="size-4 text-emerald-600" />
        ) : (
          <AlertTriangle className="size-4 text-amber-500" />
        )}
        <span>{ok ? okText : koText}</span>
      </div>
    </div>
  )
}

function DeliveryStateChip({
  isCompliant,
  isLocked,
  hasRisks,
}: {
  isCompliant: boolean
  isLocked: boolean
  hasRisks: boolean
}) {
  const { t } = useTranslation("transit")
  if (isCompliant) {
    return (
      <Chip tone="good">{t("terminer.traceDelivery.badgeCompliant")}</Chip>
    )
  }
  if (!isLocked) {
    return (
      <Chip tone="muted">{t("terminer.traceDelivery.badgeOpen")}</Chip>
    )
  }
  if (hasRisks) {
    return <Chip tone="bad">{t("terminer.traceDelivery.badgeRisk")}</Chip>
  }
  return <Chip tone="warn">{t("terminer.traceDelivery.badgePartial")}</Chip>
}

function Chip({
  tone,
  children,
}: {
  tone: "good" | "warn" | "bad" | "muted"
  children: React.ReactNode
}) {
  const palette = {
    good: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    warn: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    bad: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    muted: "bg-muted text-muted-foreground",
  } as const
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${palette[tone]}`}
    >
      {children}
    </span>
  )
}

function joinRoute(pol: string | null | undefined, pod: string | null | undefined) {
  if (!pol && !pod) return "—"
  return `${pol ?? "—"} → ${pod ?? "—"}`
}
